import { useCallback } from 'react';

import { NumberDetailWhatsAppStatusIntegrationSection } from './NumberDetailWhatsAppStatusIntegrationSection';

import { Spacer, Typography, Box, Flex } from '@aircall/tractor-v2';
import { DeactivateWhatsAppConfirmationModal } from '@components/DeactivateWhatsAppConfirmationModal/DeactivateWhatsAppConfirmationModal';
import { MetaEmbeddedSignupButton } from '@components/MetaEmbeddedSignupButton';
import { LINE_CAPABILITY, LINE_PROVIDER } from '@constants/numbers.constants';
import { Placeholder } from '@dashboard/extension';
import { Loading, Paper, useGraphMutation, useToggle } from '@dashboard/library';
import {
  DeactivateWhatsAppNumber,
  DeactivateWhatsAppNumberVariables,
} from '@generated/DeactivateWhatsAppNumber';
import { getWhatsappRegistrationStatus_getWhatsappRegistrationStatus } from '@generated/getWhatsappRegistrationStatus';
import { WhatsappRegistrationState } from '@generated/globalTypes';
import {
  RegisterWhatsAppNumberVariables,
  RegisterWhatsAppNumber,
} from '@generated/RegisterWhatsAppNumber';
import { DEACTIVATE_WHATSAPP_NUMBER } from '@graphql/mutations/DeactivateWhatsAppNumberMutation';
import { REGISTER_WHATSAPP_NUMBER } from '@graphql/mutations/RegisterWhatsAppNumberMutation';
import { useFeaturesFlags } from '@hooks/useFeatures/useFeaturesFlags';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { useToast } from '@hooks/useToast';
import { Trans, useTranslation } from 'react-i18next';

interface NumberDetailWhatsAppIntegrationSectionProps {
  whatsappStatusInfo: getWhatsappRegistrationStatus_getWhatsappRegistrationStatus | undefined;
  refreshWhatsappStatus: () => Promise<void>;
  setWhatsappStatusInfo: React.Dispatch<
    React.SetStateAction<getWhatsappRegistrationStatus_getWhatsappRegistrationStatus | undefined>
  >;
  numberUUID: string | null | undefined;
  provider: string | null | undefined;
  capabilities: (string | null)[] | null | undefined;
  numberFormatted: string | null | undefined;
}

export function NumberDetailWhatsAppIntegrationSection({
  whatsappStatusInfo,
  refreshWhatsappStatus,
  setWhatsappStatusInfo,
  numberUUID,
  provider,
  capabilities,
  numberFormatted,
}: NumberDetailWhatsAppIntegrationSectionProps) {
  const { t } = useTranslation();
  const { enableWhatsappAdmin } = useFeaturesFlags();
  const [deactivateModalOpen, _, setDeactivateModalOpen, setDeactivateModalClosed] = useToggle();
  const { currentCompany } = useGlobalData();
  const isTwilioSMSCapable = !!(
    provider === LINE_PROVIDER.TWILIO && capabilities?.includes(LINE_CAPABILITY.SMS)
  );
  const toast = useToast();

  const [registerWhatsAppNumberMutation] = useGraphMutation<
    RegisterWhatsAppNumber,
    RegisterWhatsAppNumberVariables
  >(REGISTER_WHATSAPP_NUMBER);

  const [deactivateWhatsAppNumberMutation] = useGraphMutation<
    DeactivateWhatsAppNumber,
    DeactivateWhatsAppNumberVariables
  >(DEACTIVATE_WHATSAPP_NUMBER);

  const onRefreshWhatsappStatus = useCallback(async () => {
    setWhatsappStatusInfo(undefined);
    await refreshWhatsappStatus();
  }, [refreshWhatsappStatus, setWhatsappStatusInfo]);

  const onEmbeddedSignup = useCallback(
    async (phoneNumberId: string, wabaId: string) => {
      setWhatsappStatusInfo(undefined);
      await registerWhatsAppNumberMutation({
        variables: {
          input: {
            companyName: currentCompany.name,
            companyPhoneNumberId: phoneNumberId,
            companyWabaId: wabaId,
            numberUUID: numberUUID as string,
          },
        },
        onError: (error) => {
          toast.showToast({
            message: error.message,
            variant: 'critical',
            dismissIn: 60000,
          });
        },
      });
      await refreshWhatsappStatus();
    },
    [
      currentCompany.name,
      numberUUID,
      registerWhatsAppNumberMutation,
      refreshWhatsappStatus,
      setWhatsappStatusInfo,
      toast,
    ]
  );

  const deactivateWhatsAppCallback = useCallback(async () => {
    setDeactivateModalClosed();
    setWhatsappStatusInfo(undefined);
    await deactivateWhatsAppNumberMutation({
      variables: { input: { numberUUID: numberUUID as string } },
      onError: (error) => {
        toast.showToast({
          message: error.message,
          variant: 'critical',
          dismissIn: 60000,
        });
      },
    });
    await refreshWhatsappStatus();
  }, [
    numberUUID,
    deactivateWhatsAppNumberMutation,
    refreshWhatsappStatus,
    setDeactivateModalClosed,
    setWhatsappStatusInfo,
    toast,
  ]);

  return enableWhatsappAdmin ? (
    <>
      <Paper
        title={t('number_details.integrations.whatsapp_integration.title')}
        subtitle={<Trans i18nKey='number_details.integrations.whatsapp_integration.subtitle' />}
      >
        {whatsappStatusInfo?.status ? (
          <>
            <Box
              minWidth='0'
              style={{ wordBreak: 'break-word', textAlign: 'start' }}
              data-test='status-message'
            >
              <NumberDetailWhatsAppStatusIntegrationSection
                whatsappStatusInfo={whatsappStatusInfo}
                onRefreshWhatsappStatus={onRefreshWhatsappStatus}
                numberFormatted={numberFormatted}
              />
            </Box>

            {whatsappStatusInfo.status === WhatsappRegistrationState.ONLINE &&
            whatsappStatusInfo.wabaId ? (
              <>
                <Typography
                  variant='headingBoldS'
                  color='text-base'
                  margin='0'
                  paddingBottom='s'
                  marginTop='m'
                >
                  {t('number_details.integrations.whatsapp_integration.templates.title')}
                </Typography>
                <Spacer fluid direction='vertical' space='m' marginTop='m'>
                  <Box h='450px'>
                    <Placeholder
                      name='whatsapp_list_templates'
                      parameters={{ wabaId: whatsappStatusInfo.wabaId }}
                      data-test='whatsapp_list_templates'
                    />
                  </Box>
                </Spacer>
                <Typography marginTop='m'>
                  {t(`number_details.integrations.whatsapp_integration.deactivate_message`)}
                </Typography>
              </>
            ) : null}
            <Flex alignItems='column' gap='xxxs' marginTop='xs'>
              <MetaEmbeddedSignupButton
                embeddedSignupCallback={onEmbeddedSignup}
                deactivateWhatsAppCallback={setDeactivateModalOpen}
                status={whatsappStatusInfo.status}
                isTwilioSMSCapable={isTwilioSMSCapable}
              />
            </Flex>
          </>
        ) : (
          <Box minWidth='0' style={{ wordBreak: 'break-word', textAlign: 'start' }}>
            <Loading data-test='whatsapp-integration-loading' />
          </Box>
        )}
      </Paper>
      <DeactivateWhatsAppConfirmationModal
        data-test='deactivate-whatsapp-confirmation-modal'
        show={deactivateModalOpen}
        onDeactivateClick={deactivateWhatsAppCallback}
        onCancelClick={setDeactivateModalClosed}
      />
    </>
  ) : null;
}
